import React, { useState, Fragment } from 'react'
import { Action } from '../../../legacy-design-system/action'
import { Menu, PopoverMenu } from '../../../legacy-design-system/popover'
import { SuppressDialog } from './suppress-dialog'
import { PureQueryOptions } from 'apollo-client'
import { hasRole, Role } from '../../roles'
import { getCreateVirtualPathRoute } from '../../route'
import { CreateRuleExclusionDialog } from './create-rule-exclusion-dialog'
import { BlockIpDialog } from '../../block-ip-dialog/block-ip-dialog'
import {
  ManageSourceNameDialog,
  SourceName
} from '../../../components/source-names'
import { MenuAction, MenuLinkAction } from './menu-action'
import { UserInputType } from '../shared'
import {
  AgentLanguage,
  AgentLanguageKey
} from '../../agent-language/agent-language'
import { IconSeeMore } from '@contrast-security-inc/design-system-foundations'

export const moreActionTestID = 'attack-event-more-action-test-id'
export const blockIpActionTestID = 'attack-event-block-ip-test-id'
export const manageSourceNameTestId = 'manage-source-name-action'

interface AttackEventRowActionMoreProps {
  organizationID: string
  rowData: {
    id: string
    displayName: string
    sourceIP?: string
    application: {
      id: string
      role?: Role
      name: string
      language: AgentLanguageKey
    }
    type: string
    url?: string
    hasExclusion: boolean
    sourceName: SourceName | undefined
    userInput?: {
      name?: string
      type: UserInputType
    }
    ruleUuid: string
  }
  isOpen?: boolean
  isRowHovered: boolean
  refresh: () => Promise<any>
  refetchQueries: PureQueryOptions[]
  isFreemium: boolean
}

type DialogType =
  | 'suppress-event'
  | 'manage-source'
  | 'block-ip'
  | 'create-exclusion'

export const AttackEventRowMoreAction = ({
  organizationID,
  rowData: {
    id: eventUuid,
    displayName,
    sourceIP,
    application: { id: applicationID, role, name, language },
    url,
    type,
    sourceName,
    hasExclusion,
    userInput,
    ruleUuid
  },
  refresh,
  refetchQueries,
  isRowHovered,
  isFreemium
}: AttackEventRowActionMoreProps) => {
  const [openDialog, setOpenDialog] = useState<DialogType | undefined>(
    undefined
  )
  const close = () => setOpenDialog(undefined)

  return (
    <Fragment>
      <PopoverMenu
        renderTarget={() => (
          <Action
            data-testid={moreActionTestID}
            icon={IconSeeMore}
            iconTooltipMessage={{ id: 'action_more' }}
            iconTooltipPlacement="top"
          />
        )}
        renderMenu={() => (
          <Menu>
            <MenuAction
              onClick={() => setOpenDialog('suppress-event')}
              i18nTitleId="title_attackEvents_suppressEvent"
            />
            <MenuAction
              testId={manageSourceNameTestId}
              onClick={() => setOpenDialog('manage-source')}
              i18nTitleId="action_ipsManagement_manageSourceName"
            />
            <RenderBlockIPAction
              event={{
                sourceIP,
                application: { role }
              }}
            >
              <MenuAction
                testId={blockIpActionTestID}
                onClick={() => setOpenDialog('block-ip')}
                i18nTitleId="title_addIpToBlockList"
              />
            </RenderBlockIPAction>
            <RenderCreateVirtualPatchAction
              isFreemium={isFreemium}
              event={{
                type,
                url,
                application: { role }
              }}
            >
              <MenuLinkAction
                href={getCreateVirtualPathRoute(organizationID, eventUuid)}
                i18nTitleId="title_attackEvents_createVirtualPatch"
              />
            </RenderCreateVirtualPatchAction>
            <RenderCreateRuleExclusionAction
              event={{
                type,
                hasExclusion,
                application: { role }
              }}
            >
              <MenuAction
                onClick={() => setOpenDialog('create-exclusion')}
                i18nTitleId="title_addExclusion"
              />
            </RenderCreateRuleExclusionAction>
          </Menu>
        )}
        placement="bottom"
        close={!isRowHovered}
      />
      <SuppressDialog
        organizationID={organizationID}
        event={{ eventUuid, displayName, sourceIP }}
        refetchQueries={refetchQueries}
        refresh={refresh}
        isOpen={openDialog === 'suppress-event'}
        setIsOpen={close}
      />
      <BlockIpDialog
        organizationUuid={organizationID}
        ip={sourceIP}
        isOpen={openDialog === 'block-ip'}
        closeDialog={close}
      />
      <CreateRuleExclusionDialog
        organizationUuid={organizationID}
        isOpen={openDialog === 'create-exclusion'}
        closeDialog={close}
        attackEvent={{ uuid: eventUuid, sourceIP, url, userInput, ruleUuid }}
        application={{
          id: applicationID,
          name,
          language: AgentLanguage.fromKey(language).getValue()
        }}
        onExclusionAdded={() => {
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          refresh()
        }}
      />
      <ManageSourceNameDialog
        sourceIP={sourceIP || ''}
        sourceName={sourceName}
        organizationUuid={organizationID}
        isOpen={openDialog === 'manage-source'}
        closeDialog={close}
        refresh={refresh}
      />
    </Fragment>
  )
}

export function RenderBlockIPAction({
  event: {
    sourceIP,
    application: { role }
  },
  children
}: {
  readonly event: {
    readonly sourceIP?: string
    readonly application: {
      readonly role?: Role
    }
  }
  readonly children: React.ReactElement
}) {
  return sourceIP && role && hasRole(role, 'EDIT') ? children : null
}

const isProtectRuleEventType = (type: string) => type === 'PR'

function RenderCreateVirtualPatchAction({
  isFreemium,
  event: {
    type,
    url,
    application: { role }
  },
  children
}: {
  readonly isFreemium: boolean
  readonly event: {
    readonly type: string
    readonly url?: string
    readonly application: {
      readonly role?: Role
    }
  }
  readonly children: React.ReactElement
}) {
  return !isFreemium &&
    url &&
    isProtectRuleEventType(type) &&
    role &&
    hasRole(role, 'RULES_ADMIN')
    ? children
    : null
}

export function RenderCreateRuleExclusionAction({
  event: {
    hasExclusion,
    type,
    application: { role }
  },
  children
}: {
  readonly event: {
    readonly hasExclusion: boolean
    readonly type: string
    readonly application: {
      readonly role?: Role
    }
  }
  readonly children: React.ReactElement
}) {
  return isProtectRuleEventType(type) &&
    !hasExclusion &&
    role &&
    hasRole(role, 'RULES_ADMIN')
    ? children
    : null
}
